/**
 * Stair Tool - Component Based Stair Creation
 * 
 * Creates a stair assembly consisting of Runs and Landings.
 * - Click-Click to create a Run.
 * - Next Click auto-creates a Landing from previous run end to new run start.
 * - Creates a container 'Stair' element with sub-components.
 */

import { v4 as uuidv4 } from 'uuid';
import { StairElement, StairRun, StairLanding } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class StairTool extends BaseTool {
    id = 'stair';
    name = 'Stair';
    cursor = 'crosshair';

    private currentStairId: string | null = null;
    private activeRunStart: THREE.Vector3 | null = null;
    private lastRunEnd: THREE.Vector3 | null = null;
    // private tempRunEnd: THREE.Vector3 | null = null; // Unused

    // Configuration
    private width = 1.0;
    private riserHeight = 0.18; // mm
    private treadDepth = 0.28; // mm

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        // State Machine
        if (!this.currentStairId) {
            // Start a new Stair Assembly
            this.startNewStair(snapped);
            this.activeRunStart = snapped;
            // this.tempRunEnd = snapped;
        } else {
            if (this.activeRunStart) {
                // Finish Current Run
                this.commitRun(this.activeRunStart, snapped);

                // Prepare for next action
                this.lastRunEnd = snapped;
                this.activeRunStart = null;
                // this.tempRunEnd = null;

                // Update Preview to show "Ready for next run" (Landing preview?)
                this.updatePreview(snapped);
            } else {
                // Start Next Run (Auto-Landing will happen)
                // If we click somewhere else, we start a new run FROM there.
                // The gap is filled by a landing.
                this.activeRunStart = snapped;
                // this.tempRunEnd = snapped;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            // this.tempRunEnd = snapped;

            if (this.currentStairId) {
                this.updatePreview(snapped);
            }
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            // Finish Assembly
            if (this.currentStairId) {
                this.currentStairId = null;
                this.activeRunStart = null;
                this.lastRunEnd = null;
                this.context?.appStore.setToolPreview(null);
            }
        } else if (event.key === 'Escape') {
            if (this.activeRunStart) {
                // Cancel current run
                this.activeRunStart = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                // Finish or Cancel assembly
                this.currentStairId = null;
                this.lastRunEnd = null;
                this.context?.appStore.setToolPreview(null);
            }
        }
    }

    private startNewStair(_startPoint: THREE.Vector3) {
        if (!this.context) return;

        const stairId = uuidv4();
        this.currentStairId = stairId;

        const stair: StairElement = {
            id: stairId,
            category: 'Stairs',
            type: 'Assembled Stair',
            parameters: {
                'Base Level': 'Level 1',
                'Top Level': 'Level 2',
                'Actual Riser Height': `${this.riserHeight} m`,
                'Actual Tread Depth': `${this.treadDepth} m`,
                'Run Width': `${this.width} m`
            },
            constraints: [],
            geometryData: {
                components: [],
                baseLevelId: 'Level 1',
                topLevelId: 'Level 2',
                width: this.width,
                actualRiserHeight: this.riserHeight,
                actualTreadDepth: this.treadDepth,
                desiredRiserHeight: this.riserHeight,
                desiredTreadDepth: this.treadDepth,
                riserCount: 0,
                runs: [],
                landings: []
            }
        };

        this.context.bimStore.addElement(stair);
    }

    private commitRun(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context || !this.currentStairId) return;

        const stair = this.context.bimStore.project.elements.get(this.currentStairId) as StairElement;
        if (!stair) return;

        // Auto-create Landing if there was a previous run
        if (this.lastRunEnd) {
            // Create Landing between lastRunEnd and start
            // Check if they are coincident (continuous run)
            if (this.lastRunEnd.distanceTo(start) > 0.01) {
                this.createLanding(stair, this.lastRunEnd, start);
            }
        }

        // Create Run
        const runId = uuidv4();
        // Dynamic riser count from the run's horizontal length and tread depth.
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const runLength = Math.sqrt(dx * dx + dy * dy);
        const riserCount = Math.max(2, Math.round(runLength / this.treadDepth) + 1);
        const run: StairRun = {
            id: runId,
            type: 'Run',
            style: 'Straight',
            path: [[start.x, start.y, start.z], [end.x, end.y, end.z]],
            width: this.width,
            riserCount,
            baseElevation: start.z,
            topElevation: end.z,
            beginWithRiser: true,
            endWithRiser: true,
            // Tool compatibility properties
            shape: 'Straight',
            geometryData: {
                start: [start.x, start.y, start.z],
                end: [end.x, end.y, end.z],
                width: this.width,
                riserHeight: this.riserHeight,
                treadDepth: this.treadDepth,
                riserCount,
                beginWithRiser: true,
                endWithRiser: true,
                elementId: this.currentStairId
            }
        };

        // Update Store: write into the canonical runs/landings arrays that
        // StairSolver.createStairMesh reads (components stays as a mirror for
        // legacy consumers).
        const currentComponents = stair.geometryData.components || [];
        const updatedComponents = [...currentComponents, run];
        this.context.bimStore.updateElement(this.currentStairId, {
            geometryData: {
                ...stair.geometryData,
                runs: [...(stair.geometryData.runs || []), run],
                components: updatedComponents
            }
        });
    }

    private createLanding(stair: StairElement, start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        // Simple rectangular landing logic (connects two points with a box)
        // In reality, needs to be a polygon formed by Run Widths.

        const landingId = uuidv4();

        // Mock geometry: Box around the gap

        // Vertices
        // Ideally we project the Run Widths to find intersection
        const boundary = [
            [start.x, start.y],
            [end.x, end.y],
            [end.x + 1, end.y],
            [start.x + 1, start.y]
        ] as [number, number][]; // Placeholder

        const landing: StairLanding = {
            id: landingId,
            type: 'Landing',
            boundary,
            thickness: 0.3,
            elevation: start.z,
            // Tool compatibility properties
            shape: 'Straight',
            path: boundary.map(p => [p[0], p[1], start.z] as [number, number, number]),
            width: this.width,
            geometryData: {
                boundary,
                thickness: 0.3,
                elevation: start.z,
                elementId: stair.id
            }
        };

        // Update Store: mirror into landings for StairSolver (see commitRun).
        const currentComponents = stair.geometryData.components || [];
        const updatedComponents = [...currentComponents, landing];
        this.context.bimStore.updateElement(stair.id, {
            geometryData: {
                ...stair.geometryData,
                landings: [...(stair.geometryData.landings || []), landing],
                components: updatedComponents
            }
        });
    }

    private updatePreview(currentPos: THREE.Vector3) {
        if (!this.context) return;

        const previewData: any = { type: 'stair', components: [] };

        if (this.activeRunStart) {
            // Preview current Run
            previewData.components.push({
                type: 'run',
                start: this.activeRunStart,
                end: currentPos,
                width: this.width
            });

            // Preview Auto-Landing if exists
            if (this.lastRunEnd) {
                previewData.components.push({
                    type: 'landing',
                    start: this.lastRunEnd,
                    end: this.activeRunStart,
                    width: this.width
                });
            }
        } else if (this.lastRunEnd) {
            // Preview Phantom Landing connecting to cursor?
            previewData.components.push({
                type: 'landing',
                start: this.lastRunEnd,
                end: currentPos,
                width: this.width
            });
        }

        this.context.appStore.setToolPreview(previewData);
    }
}
